// src/services/comisionesService.js
// Crear y consultar comisiones vinculadas a ventas de propiedad.
//
// Una comisión nace al crear la deuda. Su plan de hitos viene de parseEsquemaComision.
// F3 añadirá procesarDevengoComision (materializa CxP just-in-time).
// F4 añadirá procesarPagoComision (post-hook tras pago de CxP).

import {
  collection, addDoc, getDoc, getDocs, doc,
  query, where, orderBy, limit as qlimit, serverTimestamp,
} from "firebase/firestore";
import { db } from "../Firebase/firebaseconfig";
import { parseEsquemaComision } from "../helpers/comisionEsquemas";
import { registrarAuditoria } from "../helpers/auditoria";

const COLL = "comisiones";

// ─── Crear comisión ───────────────────────────────────────────────────────────

/**
 * Crea un doc en `comisiones` con el plan de hitos generado por parseEsquemaComision.
 * Se invoca justo después de que se crea la deuda de propiedad.
 *
 * @param {object} params
 * @returns {Promise<DocumentReference>}
 */
export async function crearComision({
  deudaId,
  clienteId,
  clienteNombre,
  propiedadId,                  // ← contexto para ERP / utilidad
  propiedadFolio,
  propiedadDescripcion,
  fraccionamientoId,            // ← contexto para ERP / utilidad
  fraccionamientoNombre,
  proyectoId,                   // ← contexto para ERP / utilidad
  proyectoNombre,
  obraId,                       // ← contexto opcional para ERP / utilidad
  obraNombre,
  comisionistaId,
  comisionistaNombre,
  tipoCalculo = "porcentaje",  // "porcentaje" | "monto_fijo"
  porcentaje,                   // decimal, e.g. 0.03 — presente si tipoCalculo="porcentaje"
  montoBase,                    // monto de la deuda — siempre útil para referencia
  montoTotal,                   // si ya viene calculado (monto_fijo o precalculado por el caller)
  esquemaPago,
  mensualidadesN,
  hitosCustom = [],
}) {
  // Calcular montoTotal si no viene explícito
  let total = Number(montoTotal);
  if (!total || total <= 0) {
    const pct  = Number(porcentaje);
    const base = Number(montoBase);
    if (!Number.isFinite(pct) || pct <= 0) throw new Error("Porcentaje inválido");
    if (!Number.isFinite(base) || base <= 0) throw new Error("Monto base inválido");
    total = Math.round(base * pct * 100) / 100;
  }
  const montoTotalFinal = total;

  const { hitos } = parseEsquemaComision({
    esquema: esquemaPago,
    montoTotal: montoTotalFinal,
    mensualidadesN,
    hitosCustom,
  });

  const payload = {
    deudaId,
    clienteId: clienteId || "",
    clienteNombre: clienteNombre || "",
    propiedadId: propiedadId || "",
    propiedadFolio: propiedadFolio || "",
    propiedadDescripcion: propiedadDescripcion || "",
    fraccionamientoId: fraccionamientoId || "",
    fraccionamientoNombre: fraccionamientoNombre || "",
    proyectoId: proyectoId || "",
    proyectoNombre: proyectoNombre || "",
    obraId: obraId || "",
    obraNombre: obraNombre || "",
    comisionistaId,
    comisionistaNombre: comisionistaNombre || "",
    tipoCalculo,
    porcentaje: tipoCalculo === "porcentaje" ? Number(porcentaje) : null,
    montoBase: Number(montoBase) || null,
    montoTotal: montoTotalFinal,
    esquemaPago,
    hitos,
    devengado: 0,
    pagado: 0,
    saldo: montoTotalFinal,
    estado: "activa",
    creado: serverTimestamp(),
    updatedAt: serverTimestamp(),
  };

  const ref = await addDoc(collection(db, COLL), payload);

  await registrarAuditoria({
    accion: "comisiones.crear",
    entidadTipo: "comision",
    entidadId: ref.id,
    detalle: `${comisionistaNombre} — ${propiedadFolio || deudaId}`,
    despues: { deudaId, comisionistaId, montoTotal: montoTotalFinal, tipoCalculo, esquemaPago, hitos: hitos.length },
  });

  return ref;
}

// ─── Consultas ────────────────────────────────────────────────────────────────

export async function getComision(id) {
  const snap = await getDoc(doc(db, COLL, id));
  if (!snap.exists()) return null;
  return { id: snap.id, ...snap.data() };
}

/** Comisiones de una deuda (puede haber más de una si hubo co-vendedores). */
export async function getComisionesPorDeuda(deudaId) {
  const qy = query(collection(db, COLL), where("deudaId", "==", deudaId));
  const snap = await getDocs(qy);
  return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
}

/** Comisiones de un comisionista — usado por el portal /portal/mi-cuenta. */
export async function getComisionesPorComisionista(comisionistaId, maxItems = 100) {
  const qy = query(
    collection(db, COLL),
    where("comisionistaId", "==", comisionistaId),
    orderBy("creado", "desc"),
    qlimit(maxItems)
  );
  const snap = await getDocs(qy);
  return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
}

/** Carga una comisión + sus CxPs asociadas, sin verificación de propiedad (admin).
 *
 * @param {string} comisionId
 * @returns {Promise<{ comision: object, cxpMap: object } | null>}
 */
export async function getComisionConDetalle(comisionId) {
  const snap = await getDoc(doc(db, COLL, comisionId));
  if (!snap.exists()) return null;
  const comision = { id: snap.id, ...snap.data() };

  // Recopilar CxP IDs de hitos devengados
  const cxpIds = [
    ...new Set(
      (comision.hitos || [])
        .filter((h) => h.devengado && h.cxpId)
        .map((h) => h.cxpId)
    ),
  ];

  let cxpMap = {};
  if (cxpIds.length) {
    const snaps = await Promise.all(
      cxpIds.map((id) => getDoc(doc(db, "cxp_deudas", id)).catch(() => null))
    );
    for (const s of snaps) {
      if (s?.exists()) cxpMap[s.id] = { id: s.id, ...s.data() };
    }
  }

  return { comision, cxpMap };
}

/** Vista admin: todas las comisiones, opcionalmente filtradas.
 *
 * NOTA sobre índices: las rutas con solo `where` (sin `orderBy`) no requieren
 * índice compuesto. Cuando se filtra por comisionistaId se ordena client-side
 * para evitar el índice compuesto (comisionistaId + creado).
 */
export async function listarComisiones({ estado = null, comisionistaId = null, maxItems = 500 } = {}) {
  let qy;

  if (estado) {
    // Solo where → no necesita índice compuesto; ordenamos client-side (igual que comisionistaId)
    qy = query(collection(db, COLL), where("estado", "==", estado), qlimit(maxItems));
  } else if (comisionistaId) {
    // Solo where → no necesita índice compuesto; ordenamos client-side
    qy = query(collection(db, COLL), where("comisionistaId", "==", comisionistaId), qlimit(maxItems));
  } else {
    qy = query(collection(db, COLL), orderBy("creado", "desc"), qlimit(maxItems));
  }

  const snap = await getDocs(qy);
  const docs = snap.docs.map((d) => ({ id: d.id, ...d.data() }));

  // Orden descendente por creado cuando no lo aplicó Firestore
  if (comisionistaId || estado) {
    docs.sort((a, b) => {
      const ta = a.creado?.toMillis?.() ?? 0;
      const tb = b.creado?.toMillis?.() ?? 0;
      return tb - ta;
    });
  }

  return docs;
}
